/**
 * Premium "Cinematic Hero" intro.
 *
 * Visual design:
 *  - Subtle parallax of two gradient blobs while a thin centerline draws.
 *  - Hero title slides up word-by-word with a slow zoom (Ken-Burns style).
 *  - Brand chip top-left, location stamp top-right (optional).
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const cinematicHero: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "Werde Teil von Allyver";
  const sub = inputs.subtext ?? "";
  const headSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.62;
  const subSize = typeSize("md", ctx.format) * 0.56;

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const innerId = `${clipPrefix}-inner`;
  const lineId = `${clipPrefix}-line`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const vignetteId = `${clipPrefix}-vig`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  // Word-split for staggered reveal.
  const words = headline.split(/\s+/);
  const wordHtml = words
    .map(
      (w, i) =>
        `<span id="${clipPrefix}-w${i}" style="display:inline-block;margin:0 0.22em;will-change:transform,opacity;">${escapeHtml(w)}</span>`,
    )
    .join("");

  // Hinweis: Bewusst KEINE Eyebrow ("Cinematic") und KEIN Marken-Stamp in
  // der Ecke — der Kunde soll keine Szenen-Namen oder doppelten Logo-Marker
  // sehen. Das globale Watermark (falls aktiv) übernimmt den Brand-Aufdruck.
  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
  <div id="${innerId}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 36 : 28}px;padding:96px;will-change:transform;">
    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;line-height:0.95;letter-spacing:-3px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 920 : 1500}px;text-shadow:0 0 70px ${withAlpha(accent, 0.45)};">${wordHtml}</div>

    <div id="${lineId}" style="height:2px;width:0;background:${accent};box-shadow:0 0 16px ${withAlpha(accent, 0.7)};"></div>

    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:0.5px;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;max-width:${isVertical ? 760 : 1100}px;line-height:1.35;">${escapeHtml(sub)}</div>` : ""}
  </div>

  <div id="${vignetteId}" style="position:absolute;inset:0;background:radial-gradient(ellipse at 50% 50%, transparent 50%, rgba(0,0,0,0.55) 100%);pointer-events:none;"></div>
</div>`;

  const initParts: string[] = [
    `gsap.set("#${innerId}", { scale: 1.06 });`,
    `gsap.set("#${lineId}", { width: 0 });`,
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 18 });` : "",
  ];
  for (let i = 0; i < words.length; i++) {
    initParts.push(`gsap.set("#${clipPrefix}-w${i}", { opacity: 0, y: 36, filter: "blur(12px)" });`);
  }
  const init = initParts.join("\n");

  const tweens: string[] = [];
  // Word-by-word reveal.
  words.forEach((_, i) => {
    tweens.push(
      t.to(`#${clipPrefix}-w${i}`, { opacity: 1, y: 0, filter: "blur(0px)", duration: 0.55, ease: "raw:\"power3.out\"" }, 0.2 + i * 0.08),
    );
  });
  tweens.push(t.to(`#${lineId}`, { width: 90, duration: 0.45, ease: "raw:\"power2.out\"" }, 0.2 + words.length * 0.08));
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.35 + words.length * 0.08));

  // Slow Ken-Burns zoom (entire content).
  const dwell = Math.max(0.6, clipDurationSec - 0.6);
  tweens.push(t.to(`#${innerId}`, { scale: 1, duration: dwell, ease: "raw:\"sine.inOut\"" }, 0.0));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
